import { CheckCircle2, CircleAlert, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ToastContext, type ToastInput, type ToastTone } from './toastContext'

type ToastItem = ToastInput & { id: string; tone: ToastTone }

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const timers = useRef(new Map<string, number>())

  const dismissToast = useCallback((id: string) => {
    const timer = timers.current.get(id)
    if (timer !== undefined) window.clearTimeout(timer)
    timers.current.delete(id)
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const showToast = useCallback((input: ToastInput) => {
    const id = crypto.randomUUID()
    const item: ToastItem = { ...input, id, tone: input.tone ?? 'success' }
    setItems((current) => [...current.slice(-2), item])
    const timer = window.setTimeout(() => dismissToast(id), input.durationMs ?? 4_500)
    timers.current.set(id, timer)
    return id
  }, [dismissToast])

  useEffect(() => () => {
    for (const timer of timers.current.values()) window.clearTimeout(timer)
    timers.current.clear()
  }, [])

  const value = useMemo(() => ({ showToast, dismissToast }), [dismissToast, showToast])
  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div aria-label="Notifications" className="toast-region">
          {items.map((item) => (
            <div className={`toast toast--${item.tone}`} key={item.id} role={item.tone === 'error' ? 'alert' : 'status'}>
              {item.tone === 'error' ? <CircleAlert size={18} /> : <CheckCircle2 size={18} />}
              <span>{item.message}</span>
              {item.actionLabel && item.onAction ? <button type="button" onClick={() => { item.onAction?.(); dismissToast(item.id) }}>{item.actionLabel}</button> : null}
              <button aria-label="Fermer la notification" className="toast__close" type="button" onClick={() => dismissToast(item.id)}><X size={16} /></button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}
